import { useEffect, useRef, useState } from 'react';
import {
  Mail,
  CheckSquare,
  Calendar,
  ChevronUp,
  PenSquare,
  CirclePlus,
  CalendarPlus,
  StickyNote,
  FilePlus,
} from 'lucide-react';
import { useUIStore, type MailView, type Workspace } from '@/stores/uiStore';
import { useBulkOperationsStore } from '@/stores/bulkOperationsStore';
import { useCalendarModalStore } from '@/stores/calendarModalStore';
import { useCalendarViewStore } from '@/stores/calendarViewStore';
import { MAIL_VIEWS, useMailTabState } from '@/features/mailbox/MailViewTabs';
import { CALENDAR_VIEWS } from '@/components/WorkspaceHeader';
import { navigateToMailView } from '@/features/mailbox/mailViewNavigation';
import type { CalendarViewMode } from '@/types/calendar';
import { safePushState } from '@/lib/iosPwaNavigation';
import { cn } from '@/lib/utils';

const APPS: Array<{ id: Workspace; label: string; icon: typeof Mail }> = [
  { id: 'mail', label: 'Mail', icon: Mail },
  { id: 'todo', label: 'Tasks', icon: CheckSquare },
  { id: 'notes', label: 'Notes', icon: StickyNote },
  { id: 'calendar', label: 'Calendar', icon: Calendar },
];

const DEFAULT_URLS: Record<Workspace, string> = {
  mail: '#/',
  todo: '#/tasks',
  calendar: '#/calendar',
  notes: '#/notes',
};

/**
 * The mobile dock — one bar reading "which app › which view of it": a leading
 * app pill plus the current app's tab chips (round 4, Variant A). Tapping the
 * pill flips the same bar to the four-app switcher for one choice, then it
 * flips back. The dock is one slot with four states: tabs (default), apps
 * (pill tapped), bulk actions (BulkActionBar takes the slot), and reader
 * actions (MobileActionBar takes the slot).
 */
export function MobileNavigation() {
  const activeWorkspace = useUIStore((state) => state.activeWorkspace);
  const setActiveWorkspace = useUIStore((state) => state.setActiveWorkspace);
  const mailView = useUIStore((state) => state.mailView);
  const setComposeOpen = useUIStore((state) => state.setComposeOpen);
  const triggerPrimaryAction = useUIStore((state) => state.triggerPrimaryAction);
  const readerBarActive = useUIStore((state) => state.mobileReaderBarCount > 0);
  const bulkBarActive = useBulkOperationsStore((state) => state.bulkEditMode);
  const openCreateEventDialog = useCalendarModalStore((state) => state.openCreateDialog);

  const [appsFlipped, setAppsFlipped] = useState(false);

  // Any workspace change (from the dock or elsewhere) settles the bar back to
  // its tabs state.
  useEffect(() => {
    setAppsFlipped(false);
  }, [activeWorkspace]);

  const handleAppSelect = (workspace: Workspace) => {
    setAppsFlipped(false);
    if (workspace === activeWorkspace) {
      // Re-selecting Mail from a sub-view returns to the mailbox.
      if (workspace === 'mail' && mailView !== 'mailbox') {
        navigateToMailView('mailbox');
      }
      return;
    }
    // Push the workspace default URL immediately so the URL is never stale
    // during the new workspace's initialization/loading phase
    safePushState(DEFAULT_URLS[workspace]);
    setActiveWorkspace(workspace);
  };

  const handlePrimaryAction = () => {
    if (activeWorkspace === 'mail') {
      setComposeOpen(true);
    } else if (activeWorkspace === 'calendar') {
      openCreateEventDialog();
    } else {
      triggerPrimaryAction();
    }
  };

  const getPrimaryActionIcon = () => {
    switch (activeWorkspace) {
      case 'mail':
        return PenSquare;
      case 'todo':
        return CirclePlus;
      case 'notes':
        return FilePlus;
      case 'calendar':
        return CalendarPlus;
      default:
        return PenSquare;
    }
  };

  const PrimaryIcon = getPrimaryActionIcon();
  const activeApp = APPS.find((app) => app.id === activeWorkspace) ?? APPS[0];
  const ActiveAppIcon = activeApp.icon;

  // One bottom bar at a time: while an email/thread/digest reader bar or the
  // bulk action bar is mounted, the dock yields the slot entirely.
  if (readerBarActive || bulkBarActive) {
    return null;
  }

  return (
    <>
      {appsFlipped && (
        <div
          className="fixed inset-0 z-40 bg-black/20 md:hidden"
          onClick={() => setAppsFlipped(false)}
          aria-hidden="true"
        />
      )}
      <div className="md:hidden shrink-0 surface-dark pwa-nav-safe-area relative z-50">
        {appsFlipped ? (
          <nav className="flex h-14 items-center justify-evenly px-2" aria-label="Switch app">
            {APPS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => handleAppSelect(id)}
                className={cn(
                  'flex flex-col items-center gap-0.5 rounded-2xl px-4 py-1 text-[10px] font-semibold transition-colors',
                  id === activeWorkspace
                    ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                    : 'text-sidebar-foreground/80'
                )}
              >
                <Icon className="size-5" />
                {label}
              </button>
            ))}
          </nav>
        ) : (
          <nav className="flex h-14 items-center gap-2 pl-3 pr-2" aria-label="Navigation">
            {/* App pill: names the app, flips the bar to the app switcher */}
            <button
              onClick={() => setAppsFlipped(true)}
              className="flex shrink-0 items-center gap-1.5 rounded-full bg-sidebar-accent px-3.5 py-2 font-semibold text-sidebar-accent-foreground"
              aria-label={`${activeApp.label} — switch app`}
            >
              <ActiveAppIcon className="size-4.5" />
              <ChevronUp className="size-3 opacity-60" />
            </button>

            {activeWorkspace === 'mail' ? (
              <MailDockTabs />
            ) : activeWorkspace === 'calendar' ? (
              <CalendarDockTabs />
            ) : (
              <div className="flex-1" />
            )}

            {/* FAB floats above the bar's right edge, clear of the chips */}
            <button
              onClick={handlePrimaryAction}
              className="relative z-10 -mt-9 mr-1 flex h-13 w-13 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-primary/90"
              aria-label="Create"
            >
              <PrimaryIcon className="size-6" />
            </button>
          </nav>
        )}
      </div>
    </>
  );
}

/**
 * The mail sub-view chips inside the dock. Scrolls horizontally behind an
 * edge fade when the five tabs outgrow the bar; the active chip is kept in
 * view.
 */
function MailDockTabs() {
  const { mailView, badges } = useMailTabState();

  return (
    <DockChips
      activeKey={mailView}
      items={MAIL_VIEWS.map(({ view, label }) => ({ key: view, label, badge: badges[view] }))}
      onSelect={(key) => navigateToMailView(key as MailView)}
    />
  );
}

/** Calendar's view switcher in the dock — same chips, same slot. */
function CalendarDockTabs() {
  const activeView = useCalendarViewStore((state) => state.activeView);
  const setActiveView = useCalendarViewStore((state) => state.setActiveView);

  return (
    <DockChips
      activeKey={activeView}
      items={CALENDAR_VIEWS.map(({ id, label }) => ({ key: id, label }))}
      onSelect={(key) => setActiveView(key as CalendarViewMode)}
    />
  );
}

function DockChips({
  activeKey,
  items,
  onSelect,
}: {
  activeKey: string;
  items: Array<{ key: string; label: string; badge?: number }>;
  onSelect: (key: string) => void;
}) {
  const activeRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [activeKey]);

  return (
    <div
      className="flex min-w-0 flex-1 items-center gap-0.5 overflow-x-auto scrollbar-none"
      style={{
        maskImage: 'linear-gradient(90deg, #000 88%, transparent)',
        WebkitMaskImage: 'linear-gradient(90deg, #000 88%, transparent)',
      }}
    >
      {items.map(({ key, label, badge }) => {
        const isActive = activeKey === key;
        return (
          <button
            key={key}
            ref={isActive ? activeRef : undefined}
            onClick={() => onSelect(key)}
            className={cn(
              'flex shrink-0 items-center gap-1 rounded-full px-3 py-1.5 text-[13px] font-semibold whitespace-nowrap transition-colors',
              isActive
                ? 'bg-sidebar-primary text-sidebar-primary-foreground'
                : 'text-sidebar-foreground/80'
            )}
          >
            {label}
            {badge ? (
              <span
                className={cn(
                  'rounded-full px-1.5 text-[10px] font-semibold leading-4',
                  isActive ? 'bg-black/10' : 'bg-white/15'
                )}
              >
                {badge}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
